import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, DownloadIcon, MailIcon, EyeIcon, EditIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function Invoices() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data - in a real app, this would come from an API
  const invoices: Invoice[] = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: 'Rs.',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    items: [],
    subtotal: 125000,
    total: 125000
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    customerName: 'Tech Solutions Ltd',
    customerEmail: 'accounts@techsolutions.lk',
    customerPhone: '+60 11 234 5678',
    amount: 87500,
    currency: 'Rs.',
    status: 'sent',
    dueDate: '2024-02-01',
    issueDate: '2024-01-15',
    description: 'Consulting services',
    items: [],
    subtotal: 87500,
    total: 87500
  }, {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    customerName: 'Global Enterprises',
    customerEmail: 'finance@global.com',
    amount: 250000,
    currency: 'Rs.',
    status: 'overdue',
    dueDate: '2024-01-20',
    issueDate: '2024-01-05',
    description: 'Product delivery',
    items: [],
    subtotal: 250000,
    total: 250000
  }, {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    customerName: 'Innovation Hub',
    customerEmail: 'billing@innovationhub.lk',
    amount: 45000,
    currency: 'Rs.',
    status: 'draft',
    dueDate: '2024-02-15',
    issueDate: '2024-01-25',
    description: 'Software license',
    items: [],
    subtotal: 45000,
    total: 45000
  }];
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-7192">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = filteredInvoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
  return <div className="space-y-6" data-id="element-7193">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-7194">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-7195">
          <div className="text-sm font-medium text-gray-500" data-id="element-7196">
            Total Invoices
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-7197">
            {filteredInvoices.length}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-7198">
          <div className="text-sm font-medium text-gray-500" data-id="element-7199">Total Amount</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-7200">
            MYR {totalAmount.toLocaleString('en-MY')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-7201">
          <div className="text-sm font-medium text-gray-500" data-id="element-7202">Paid</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-7203">
            MYR {paidAmount.toLocaleString('en-MY')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-7204">
          <div className="text-sm font-medium text-gray-500" data-id="element-7205">Overdue</div>
          <div className="mt-2 text-3xl font-semibold text-red-600" data-id="element-7206">
            MYR {overdueAmount.toLocaleString('en-MY')}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-7207">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-7208">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-7209">
            <div className="flex-1 max-w-lg" data-id="element-7210">
              <div className="relative" data-id="element-7211">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-7212" />
                <input type="text" placeholder="Search invoices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7213" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-7214">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7215">
                <option value="all" data-id="element-7216">All Status</option>
                <option value="draft" data-id="element-7217">Draft</option>
                <option value="sent" data-id="element-7218">Sent</option>
                <option value="paid" data-id="element-7219">Paid</option>
                <option value="overdue" data-id="element-7220">Overdue</option>
                <option value="cancelled" data-id="element-7221">Cancelled</option>
              </select>
              <button onClick={() => navigate('/invoice-form')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-7222">
                <PlusIcon size={20} data-id="element-7223" />
                New Invoice
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-7224">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-7225">
            <thead className="bg-gray-50" data-id="element-7226">
              <tr data-id="element-7227">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7228">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7229">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7230">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7231">
                  Issue Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7232">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7233">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7234">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-7235">
              {filteredInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-7236">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7237">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7238">
                      {invoice.invoiceNumber}
                    </div>
                    {invoice.description && <div className="text-sm text-gray-500" data-id="element-7239">
                        {invoice.description}
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7240">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7241">
                      {invoice.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-7242">
                      {invoice.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7243">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7244">
                      MYR {invoice.amount.toLocaleString('en-MY')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7245">
                    {new Date(invoice.issueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-7246">
                    {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7247">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7248">
                    <div className="flex items-center justify-end gap-2" data-id="element-7249">
                      <button onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-7250">
                        <EyeIcon size={18} data-id="element-7251" />
                      </button>
                      <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-7252">
                        <EditIcon size={18} data-id="element-7253" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-900" title="Send Email" data-id="element-7254">
                        <MailIcon size={18} data-id="element-7255" />
                      </button>
                      <button className="text-green-600 hover:text-green-900" title="Download" data-id="element-7256">
                        <DownloadIcon size={18} data-id="element-7257" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredInvoices.length === 0 && <div className="text-center py-12" data-id="element-7258">
            <p className="text-gray-500" data-id="element-7259">No invoices found</p>
          </div>}
      </div>
    </div>;
}